import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../../utils/helper';
import type { GuideNavItem } from './navConfig';
import LinkIcon from "../../assets/images/icon/link_arrow_icon.svg";

interface SidebarItemProps {
  isChildren?: boolean;
  item: GuideNavItem;
  depth?: number;
  activeId?: string;
  onItemClick?: (item: GuideNavItem) => void;
}

function hasActive(item: GuideNavItem, activeId?: string): boolean {
  if (!activeId) return false;
  if (item.id === activeId) return true;
  return (item.items ?? []).some((child) => hasActive(child, activeId));
}

const DEFAULT_OPEN_PARENT_IDS = new Set(['principles', 'creative']);

export default function SidebarItem({
  isChildren,
  item,
  depth = 0,
  activeId,
  onItemClick,
}: SidebarItemProps) {
  const isActive = item.id === activeId;
  const initiallyOpen = useMemo(
    () => hasActive(item, activeId) || DEFAULT_OPEN_PARENT_IDS.has(item.id),
    [item, activeId],
  );
  const [open, setOpen] = useState(initiallyOpen);
  const hasChildren = Boolean(item.items?.length);
  const navigate = useNavigate();

  const padding = 12 + depth * 12;
  const isInternalLink = item.href?.startsWith('/');
  const baseClassName = cn(
    'w-full flex items-center gap-2 rounded-md hover:bg-gray-50 text-[14px]',
    hasChildren ? 'justify-between' : 'justify-between',
    isActive ? 'bg-[#E6F0FE] font-[500] text-black' : 'text-black',
    isChildren ? '' : 'font-[600]',
  );
  const sharedStyle = { paddingLeft: padding, paddingRight: 12, height: 40 };

  const handleItemClick = () => {
    onItemClick?.(item);
  };

  const scrollToTop = () => {
    if (typeof window === 'undefined') {
      return;
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  useEffect(() => {
    if (initiallyOpen) {
      setOpen(true);
    }
  }, [initiallyOpen]);

  if (hasChildren) {
    const handleParentClick = (event: MouseEvent<HTMLButtonElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-role="sidebar-toggle"]')) {
        event.preventDefault();
        setOpen((value) => !value);
        return;
      }

      handleItemClick();
      setOpen(true);

      if (!item.href) {
        return;
      }

      scrollToTop();

      if (isInternalLink) {
        navigate(item.href);
        return;
      }

      if (typeof window !== 'undefined') {
        window.location.href = item.href;
      }
    };

    return (
      <div>
        <button
          type="button"
          className={baseClassName}
          style={sharedStyle}
          onClick={handleParentClick}
          aria-expanded={open}
        >
          <span className="truncate">{item.label}</span>
          <span
            data-role="sidebar-toggle"
            className="flex h-6 w-6 items-center justify-center -mr-1"
          >
            <svg
              aria-hidden
              data-role="sidebar-toggle"
              className={cn('h-4 w-4 shrink-0 transition-transform', open ? 'rotate-90' : 'rotate-0', 'text-gray-400')}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M7 5l6 5-6 5V5z" />
            </svg>
          </span>
        </button>

        {open && (
          <div
              className=" mt-1 space-y-1 pl-[24px]">
            {item.items!.map((child) => (
              <SidebarItem
                isChildren
                key={child.id}
                item={child}
                depth={depth}
                activeId={activeId}
                onItemClick={onItemClick}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (item.href) {
    if (isInternalLink) {
      return (
        <Link
          to={item.href}
          className={baseClassName}
          style={sharedStyle}
          onClick={() => {
            handleItemClick();
            scrollToTop();
          }}
        >
          <span className="truncate">{item.label}</span>
        </Link>
      );
    }

    return (
      <a
        href={item.href}
        className={baseClassName}
        style={sharedStyle}
        onClick={() => {
          handleItemClick();
          scrollToTop();
        }}
      >
        <span className="truncate">{item.label}</span>
        {item.href?.includes('https') && <img src={LinkIcon} />}
      </a>
    );
  }

  return (
    <button type="button" className={baseClassName} style={sharedStyle} onClick={handleItemClick}>
      <span className="truncate">{item.label}</span>
    </button>
  );
}
